import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import parse from 'html-react-parser';
import { RefObject, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Checkbox } from '@/components/core/Checkbox';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import { RadioGroup, RadioGroupItem } from '@/components/core/RadioGroup';
import StepProgress from '@/components/core/StepProgress/StepProgress';
import { Textarea } from '@/components/core/Textarea';
import { Stepper, useStepper } from '@/hooks/useStepper';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';

import {
    formatCurrencyAmount,
    localizedText,
    monthlyPriceMap,
    yearlyPriceMap,
} from '../user-management.format';
import {
    fetchUserLimit,
    useAdditionalLicenseProfiles,
    useBillingAddress,
    useBuyLicenses,
    useOrderedPlanProfile,
} from '../user-management.queries';
import { BillingAddress, LicenseProfile } from '../user-management.types';
import { FormField } from './FormField';

interface BuyLicensesDialogProps {
    open: boolean;
    onClose: (userLimit?: number) => void;
}

export function BuyLicensesDialog({ open, onClose }: BuyLicensesDialogProps) {
    const { t } = useTranslation();
    return (
        <Stepper steps={[
            t('USER_MANAGEMENT.SELECT_PACKAGE'),
            t('BILLING.CUSTOMER_CONTACT'),
            t('BILLING.SUMMARY'),
        ]}>
            <BuyLicensesWizard open={open} onClose={onClose} />
        </Stepper>
    );
}

function BuyLicensesWizard({ open, onClose }: BuyLicensesDialogProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { currentStep, steps, nextStep, prevStep } = useStepper();
    const profilesQuery = useAdditionalLicenseProfiles();
    const planQuery = useOrderedPlanProfile();
    const addressQuery = useBillingAddress();
    const buy = useBuyLicenses();
    const firstNameRef = useRef<HTMLInputElement>(null);
    const closingRef = useRef(false);

    const { currency, symbol, language, termsUrl } = readBillingContext();
    const [selectedName, setSelectedName] = useState<string | undefined>();
    const [address, setAddress] = useState<BillingAddress>({});
    const [addressReady, setAddressReady] = useState(false);
    const [agreed, setAgreed] = useState(false);

    useEffect(() => {
        const injector = getInjector();
        const root = getRootScope(injector);
        if (root && !root.currentCurrency) {
            root.currentCurrency = injector.get('CurrencyService').getCurrencyKey(root.user?.country || 'US');
        }
    }, []);

    const profiles = profilesQuery.data ?? [];
    const selected = profiles.find((profile) => profile.name === selectedName) ?? profiles[0];

    useEffect(() => {
        if (!addressReady && addressQuery.data) {
            setAddress(addressQuery.data);
            setAddressReady(true);
        }
    }, [addressQuery.data, addressReady]);

    useEffect(() => {
        if (currentStep !== 2) {
            return;
        }
        const timer = window.setTimeout(() => firstNameRef.current?.focus(), 10);
        return () => window.clearTimeout(timer);
    }, [currentStep]);

    const finish = async (purchased: boolean) => {
        if (closingRef.current) {
            return;
        }
        closingRef.current = true;
        if (purchased) {
            toast.success('USER_MANAGEMENT.LICENSES_ADDED');
        }
        try {
            const userLimit = await fetchUserLimit();
            onClose(userLimit);
        } catch (error) {
            logger.error(error);
            onClose();
        }
    };

    const goNext = () => {
        if (currentStep === 2 && !isContactComplete(address)) {
            toast.error('Required fields missing');
            return;
        }
        if (currentStep < steps.length) {
            nextStep();
        }
    };

    const buyNow = () => {
        if (!agreed || !selected || buy.isPending) {
            if (!agreed) {
                toast.error('Required fields missing');
            }
            return;
        }
        buy.mutate({
            additionalLicense: selected.name,
            currency,
            billingAddress: address,
        }, {
            onSuccess: () => {
                finish(true);
            },
        });
    };

    const monthly = monthlyPriceMap(selected, planQuery.data?.monthlyPrice);
    const yearly = yearlyPriceMap(monthly);

    return (
        <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
            <DialogContent variant="small">
                <DialogHeader>
                    <DialogHeaderDefault onClose={(event) => {
                        event.preventDefault();
                        finish(false);
                    }}
                    >
                        {t('USER_MANAGEMENT.BUY_USER_LICENSE')}
                    </DialogHeaderDefault>
                </DialogHeader>
                <DialogDescription className="tw-sr-only">{t('USER_MANAGEMENT.BUY_USER_LICENSE')}</DialogDescription>
                <DialogBody className="tw-flex tw-max-h-[70vh] tw-flex-col tw-gap-10 tw-overflow-auto">
                    <StepProgress />
                    {profilesQuery.isLoading ? (
                        <div className="tw-flex tw-min-h-48 tw-w-full tw-flex-1 tw-items-center tw-justify-center">
                            <Loader size="lg" />
                        </div>
                    ) : (
                        <>
                            {currentStep === 1 && (
                                <PackageStep
                                    profiles={profiles}
                                    selectedName={selected?.name}
                                    language={language}
                                    currency={currency}
                                    symbol={symbol}
                                    planMonthlyPrice={planQuery.data?.monthlyPrice}
                                    footnote={t('BILLING.FOOT_NOTE')}
                                    perMonth={t('BILLING.BILLING_EXPIRATION_MONTH')}
                                    billedAnnually={t('BILLING.BILLED_ANNUALLY')}
                                    perYear={t('BILLING.PER_YEAR')}
                                    onSelect={setSelectedName}
                                />
                            )}
                            {currentStep === 2 && (
                                <ContactStep
                                    address={address}
                                    firstNameRef={firstNameRef}
                                    showErrors={addressReady}
                                    onChange={setAddress}
                                    labels={{
                                        firstName: t('BILLING.CUSTOMER_FIRST_NAME'),
                                        lastName: t('BILLING.CUSTOMER_LAST_NAME'),
                                        address: t('ADDRESS'),
                                        required: t('BILLING.MUST_BE_PROVIDED'),
                                    }}
                                />
                            )}
                            {currentStep === 3 && selected && (
                                <SummaryStep
                                    profile={selected}
                                    address={address}
                                    language={language}
                                    monthlyText={formatCurrencyAmount(monthly, currency, symbol)}
                                    yearlyText={formatCurrencyAmount(yearly, currency, symbol)}
                                    agreed={agreed}
                                    onAgreed={setAgreed}
                                    termsHtml={t('BILLING.BILLING_TERMS', { url: termsUrl })}
                                    perYear={t('BILLING.PER_YEAR')}
                                    footnote={t('BILLING.FOOT_NOTE')}
                                />
                            )}
                        </>
                    )}
                </DialogBody>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-px-5 tw-py-3 lg:tw-px-8 tw-border-t tw-border-solid
                    tw-border-x-0 tw-border-b-0 tw-border-tertiary">
                    <div>
                        {currentStep > 1 && (
                            <Button variant="secondary" hasIcon icon={ArrowLeftIcon} onClick={prevStep}>
                                {t('PREVIOUS')}
                            </Button>
                        )}
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                        {currentStep < steps.length && (
                            <Button
                                disabled={currentStep === 2 && !isContactComplete(address)}
                                onClick={goNext}
                            >
                                {t('NEXT')}
                            </Button>
                        )}
                        {currentStep === steps.length && (
                            <Button loading={buy.isPending} disabled={!agreed || buy.isPending} onClick={buyNow}>{t('USER_MANAGEMENT.BUY')}</Button>
                        )}
                        <Button variant="secondary" onClick={() => finish(false)}>{t('CANCEL')}</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function PackageStep({
    profiles,
    selectedName,
    language,
    currency,
    symbol,
    planMonthlyPrice,
    footnote,
    perMonth,
    billedAnnually,
    perYear,
    onSelect,
}: {
    profiles: LicenseProfile[];
    selectedName?: string;
    language: string;
    currency: string;
    symbol: string;
    planMonthlyPrice?: Record<string, number>;
    footnote: string;
    perMonth: string;
    billedAnnually: string;
    perYear: string;
    onSelect: (name: string) => void;
}) {
    return (
        <div>
            <RadioGroup value={selectedName ?? ''} onValueChange={onSelect} className="tw-gap-3">
                {profiles.map((profile) => {
                    const monthly = monthlyPriceMap(profile, planMonthlyPrice);
                    const yearly = yearlyPriceMap(monthly);
                    return (
                        <label key={profile.name} htmlFor={profile.name} className="tw-flex tw-items-center tw-gap-3 tw-text-t-sm lg:tw-text-t-base tw-m-0">
                            <span className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3">
                                <RadioGroupItem id={profile.name} value={profile.name} className="tw-shrink-0" />
                                <span>{localizedText(profile.productName, language, profile.name)}</span>
                            </span>
                            <span className="tw-text-right">
                                <span>{formatCurrencyAmount(monthly, currency, symbol)}</span>
                                <span>/{perMonth}</span>
                                <span className="tw-block tw-text-t-xs lg:tw-text-t-sm tw-text-primary">
                                    {billedAnnually} ({formatCurrencyAmount(yearly, currency, symbol)} {perYear})
                                </span>
                            </span>
                        </label>
                    );
                })}
            </RadioGroup>
            <p className="tw-mt-4 tw-text-t-xs lg:tw-text-t-sm tw-text-t-secondary">{footnote}</p>
        </div>
    );
}

function ContactStep({
    address,
    firstNameRef,
    onChange,
    showErrors,
    labels,
}: {
    address: BillingAddress;
    firstNameRef: RefObject<HTMLInputElement>;
    onChange: (address: BillingAddress) => void;
    showErrors: boolean;
    labels: {
        firstName: string;
        lastName: string;
        address: string;
        required: string;
    };
}) {
    return (
        <fieldset className="tw-flex tw-flex-col tw-gap-10 tw-border-0 tw-p-0">
            <FormField
                htmlFor="billingAddress.firstName"
                label={`${labels.firstName} *`}
                error={showErrors && !address.firstName ? labels.required : undefined}
            >
                <Input
                    ref={firstNameRef}
                    id="billingAddress.firstName"
                    value={address.firstName ?? ''}
                    required
                    onChange={(event) => onChange({ ...address, firstName: event.target.value })}
                />
            </FormField>
            <FormField
                htmlFor="billingAddress.name"
                label={`${labels.lastName} *`}
                error={showErrors && !address.name ? labels.required : undefined}
            >
                <Input
                    id="billingAddress.name"
                    value={address.name ?? ''}
                    required
                    onChange={(event) => onChange({ ...address, name: event.target.value })}
                />
            </FormField>
            <FormField
                htmlFor="organizationAddress"
                label={`${labels.address} *`}
                alignTop
                error={showErrors && !address.address ? labels.required : undefined}
            >
                <Textarea
                    id="organizationAddress"
                    name="billingAddress.address"
                    rows={10}
                    required
                    value={address.address ?? ''}
                    onChange={(event) => onChange({ ...address, address: event.target.value })}
                />
            </FormField>
        </fieldset>
    );
}

function SummaryStep({
    profile,
    address,
    language,
    monthlyText,
    yearlyText,
    agreed,
    onAgreed,
    termsHtml,
    perYear,
    footnote,
}: {
    profile: LicenseProfile;
    address: BillingAddress;
    language: string;
    monthlyText: string;
    yearlyText: string;
    agreed: boolean;
    onAgreed: (agreed: boolean) => void;
    termsHtml: string;
    perYear: string;
    footnote: string;
}) {
    const { t } = useTranslation();
    return (
        <div className="tw-flex tw-flex-col tw-gap-5 tw-text-t-sm lg:tw-text-t-base">
            <div className="tw-flex tw-flex-col tw-gap-1">
                <h3 className="tw-text-t-sm lg:tw-text-t-base tw-font-bold tw-m-0">{t('USER_MANAGEMENT.CHOSEN_LICENSES')}</h3>
                <SummaryTable rows={[
                    [t('USER_MANAGEMENT.PACKAGE'), localizedText(profile.productName, language, profile.name)],
                    [t('PRICE'), `${monthlyText} (${yearlyText} ${perYear})`],
                    [t('BILLING.PRODUCT_DESCRIPTION'), localizedText(profile.marketingText, language)],
                ]}
                />
            </div>
            <p className="tw-text-t-xs lg:tw-text-t-sm tw-text-t-secondary">{footnote}</p>
            <div className="tw-flex tw-flex-col tw-gap-1">
                <h3 className="tw-text-t-sm lg:tw-text-t-base tw-font-bold tw-m-0">{t('BILLING.PERSONAL_DATA')}</h3>
                <SummaryTable rows={[
                    [t('BILLING.CUSTOMER_FIRST_NAME'), address.firstName ?? ''],
                    [t('BILLING.CUSTOMER_LAST_NAME'), address.name ?? ''],
                    [t('BILLING.CUSTOMER_ADDRESS'), address.address ?? ''],
                ]}
                />
            </div>
            <label className="tw-flex tw-items-center tw-gap-2" htmlFor="payment_profile_agreement_terms">
                <Checkbox
                    id="payment_profile_agreement_terms"
                    isChecked={agreed}
                    onCheckedChange={(checked) => onAgreed(checked === true)}
                />
                <span>{parse(termsHtml)}*</span>
            </label>
        </div>
    );
}

function SummaryTable({ rows }: { rows: Array<[string, string]> }) {
    return (
        <table className="tw-w-full tw-text-t-sm lg:tw-text-t-base">
            <tbody>
                {rows.map(([label, value]) => (
                    <tr key={label} className="tw-border-b tw-border-x-0 tw-border-t-0 tw-border-solid tw-border-tertiary">
                        <th className="tw-py-2 tw-pr-4 tw-text-left tw-font-normal tw-text-t-secondary tw-text-t-sm tw-w-1/3">{label}</th>
                        <td className="tw-py-2 tw-break-words">{value}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function isContactComplete(address: BillingAddress): boolean {
    return Boolean(address.firstName?.trim())
        && Boolean(address.name?.trim())
        && Boolean(address.address?.trim());
}

function readBillingContext(): { currency: string; symbol: string; language: string; termsUrl: string } {
    const injector = getInjector();
    const root = getRootScope(injector);
    const currencyService = injector.get('CurrencyService');
    const country = root?.user?.country || 'US';
    const currency = root?.currentCurrency || currencyService.getCurrencyKey(country);
    const termsUrl = root.customSiteTermsOfService || root.systemSettings?.TERMS_AND_CONDITIONS_URL || '';
    return {
        currency,
        symbol: currencyService.getCurrencySymbol(currency) ?? '',
        language: root.language || 'en',
        termsUrl: typeof termsUrl === 'string' ? termsUrl : '',
    };
}
